/* eslint-disable no-useless-escape */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import Answer from '../../components/Answer';
import { AnswerPopupWrapper, QuestionWrapper } from './OnBoardingQuestion.style';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAnswerConditionsSelector,
  getAnswerPopup,
  getAvailableVars,
  getCurrentStep,
  getOnBoardingID,
  getOnBoardingName,
  getQuestionById,
  getSelectedAnswersByQuestionId,
  getStepNumber,
  getVars,
  isLoading,
} from '../../../../redux/onBoarding/selector';
import { addSteps, addVars, answerOnClick, removeSteps, removeVars } from '../../../../redux/onBoarding/reducer';
import {
  OnBoardingAnswer,
  OnBoardingStepType,
  OnBoardingTypes,
  OnBoardingVars,
} from '../../../../redux/onBoarding/types';
import { IonContent, IonPage, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react';
import { Container, CustomButton } from '@mindly/ui-components';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import OnBoardingFooter from '../../components/OnBoardingFooter';
import useOnBoardingNextStep from '../../../../hooks/useOnBoardingNextStep';
import OnBoardingHeader from '../../components/OnBoardingHeader/OnBoardingHeader';
import useOnBoardingCurrentStep from '../../../../hooks/useOnBoardingCurrentStep';
import { FormattedMessage } from 'react-intl';
import { ImpactStyle } from '@capacitor/haptics';
import { FunnelTypeEnum, useAnalytics } from 'src/shared';
import { setSplashColor } from 'src/shared/utils/splashColor';
import { globalVariables } from 'src/redux/firestoreGlobalVariables/selector';
import { useTranslation } from 'react-i18next';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

interface OnBoardingQuestionProps extends RouteComponentProps<{ boardId: string; id: string }> {}

const OnBoardingQuestion: FC<OnBoardingQuestionProps> = ({ match }) => {
  const { id } = match.params;

  const [isPageView, setIsPageView] = useState(false);
  const [isEventTracked, setIsEventTracked] = useState(false);

  const pageCounterRef = useRef(0);
  const isHandleNextStep = useRef(false);
  const { i18n } = useTranslation();

  const currentQuestion = useSelector(getQuestionById(id));
  const selectedAnswers = useSelector(getSelectedAnswersByQuestionId(id));
  const answerPopup = useSelector(getAnswerPopup(id));
  const currentStep = useSelector(getCurrentStep);
  const availableVars = useSelector(getAvailableVars);
  const answersVars = useSelector(getVars);
  const isLoadingSelector = useSelector(isLoading);
  const answerConditions = useSelector(getAnswerConditionsSelector);
  const onBoardingName = useSelector(getOnBoardingName);
  const stepNumber = useSelector(getStepNumber);
  const onBoardingId = useSelector(getOnBoardingID);
  const globalVars = useSelector(globalVariables);

  const history = useHistory();

  const dispatch = useDispatch();

  const { trackPredefinedEvent } = useAnalytics();

  const nextStepAction = useOnBoardingNextStep();
  const currentStepAction = useOnBoardingCurrentStep({
    ...match.params,
    type: OnBoardingTypes.QUESTION,
  });

  useIonViewDidEnter(() => {
    currentStepAction();

    setIsPageView(true);
    setIsEventTracked(false);

    setSplashColor('#fffffe');

    isHandleNextStep.current = false;
  }, [currentStepAction, id, currentStep]);

  useIonViewDidLeave(() => {
    setIsPageView(false);
    setIsEventTracked(false);

    pageCounterRef.current = 0;
    isHandleNextStep.current = false;
  });

  useEffect(() => {
    if (!isPageView) return;
    if (pageCounterRef.current > 0) return;
    pageCounterRef.current += 1;
  }, [isPageView, stepNumber, onBoardingName, pageCounterRef]);

  const handleAnalyticEvent = () => {
    if (!onBoardingId || !currentQuestion) return;

    trackPredefinedEvent('FUNNEL_SCREEN_VIEWED', {
      funnel_id: onBoardingId,
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: id,
      screen_name: currentQuestion.question,
      screen_order: stepNumber,
    });
    setIsEventTracked(true);
  };

  useEffect(() => {
    if (!isPageView) return;
    if (isLoadingSelector) return;
    if (!onBoardingId || !onBoardingName || !currentQuestion || !stepNumber) return;
    if (isEventTracked) return;

    handleAnalyticEvent();
  }, [isPageView, isLoadingSelector, onBoardingId, onBoardingName, currentQuestion, stepNumber, isEventTracked]);

  const handleAnswer = useCallback(
    (answerId) => {
      if (!currentQuestion) return;

      dispatch(answerOnClick({ answerId, questionId: currentQuestion.id }));
      isHandleNextStep.current = true;
    },
    [currentQuestion, dispatch]
  );

  useEffect(() => {
    setSplashColor('#fffffe');
  }, []);

  useEffect(() => {
    if (!isPageView) return;
    if (!selectedAnswers?.length || !currentStep) return;
    if (currentStep.id !== id) return;
    let needAddSteps: OnBoardingStepType[] = [];
    let needRemoveSteps: OnBoardingStepType[] = [];
    let needAddVars: OnBoardingVars = {};

    currentStep?.answers?.forEach((a) => {
      if (a.next_steps && selectedAnswers.includes(a.id)) {
        needAddSteps = [...needAddSteps, ...a.next_steps.map((s) => ({ id: s.id, type: s.type }))];
      } else if (a.next_steps) {
        needRemoveSteps = [...needRemoveSteps, ...a.next_steps.map((s) => ({ id: s.id, type: s.type }))];
      }
    });

    selectedAnswers.forEach((a) => {
      const vars = availableVars.find((v) => v.id === a)?.vars;
      if (vars?.length) {
        needAddVars = {
          ...needAddVars,
          ...vars.reduce((acc, v) => ({ ...acc, [v.name]: v.value }), {}),
        };
      }
    });

    //Delete unused vars
    const notSelectedAnswers = currentQuestion?.answers.filter((a) => !selectedAnswers.find((s) => s === a.id));
    notSelectedAnswers?.forEach((a) => {
      const vars = availableVars.find((v) => v.id === a.id)?.vars;
      vars?.length && dispatch(removeVars(vars));
    });

    needRemoveSteps.length && dispatch(removeSteps(needRemoveSteps));
    needAddSteps.length &&
      dispatch(
        addSteps({
          nextSteps: needAddSteps,
          currentStep: { id: currentStep.id, type: currentStep.type },
        })
      );
    dispatch(addVars(needAddVars));

    if (
      currentQuestion?.hideContinueButton === true &&
      isHandleNextStep.current &&
      history.location.state?.isNextButton
    ) {
      trackPredefinedEvent('FUNNEL_ANSWERS', {
        funnel_id: onBoardingId ?? '',
        funnel_name: onBoardingName,
        funnel_type:
          onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
            ? FunnelTypeEnum.DEFAULT
            : FunnelTypeEnum.WEB_TO_APP,
        screen_id: id,
        screen_name: currentQuestion?.question ?? '',
        screen_order: stepNumber,
        answers: selectedAnswers?.reduce(
          (acc, answer) => {
            const option = currentQuestion?.answers?.find((o) => o.id === answer);
            if (option) {
              acc.push({ id: option.id, question: '', answer: option.title });
            }
            return acc;
          },
          [] as { id: string; question?: string; answer: string }[]
        ),
      });

      isHandleNextStep.current = false;
      setTimeout(async () => {
        isomorficHapticsImpact({ style: ImpactStyle.Light });
        await nextStepAction();
      }, 150);
    }
  }, [
    selectedAnswers,
    currentQuestion,
    currentStep,
    availableVars,
    dispatch,
    id,
    nextStepAction,
    onBoardingId,
    onBoardingName,
    trackPredefinedEvent,
    stepNumber,
    isPageView,
  ]);

  const handleNextStep = useCallback(async () => {
    trackPredefinedEvent('FUNNEL_ANSWERS', {
      funnel_id: onBoardingId ?? '',
      funnel_name: onBoardingName,
      funnel_type:
        onBoardingId === globalVars?.defaultOnBoardingId?.[i18n.language]
          ? FunnelTypeEnum.DEFAULT
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: id,
      screen_name: currentQuestion?.question ?? '',
      screen_order: stepNumber,
      answers: selectedAnswers?.reduce(
        (acc, answer) => {
          const option = currentQuestion?.answers?.find((o) => o.id === answer);
          if (option) {
            acc.push({ id: option.id, question: '', answer: option.title });
          }
          return acc;
        },
        [] as { id: string; question?: string; answer: string }[]
      ),
    });

    await nextStepAction();
  }, [
    nextStepAction,
    selectedAnswers,
    currentQuestion,
    trackPredefinedEvent,
    onBoardingId,
    stepNumber,
    id,
    onBoardingName,
    globalVars,
    i18n.language,
  ]);

  const isAnswerAvailable = useCallback(
    (answer: OnBoardingAnswer) => {
      const conditions = answerConditions?.find((c) => c.id === answer.id);

      if (!conditions) return true;

      const conditionsArr = conditions.conditions.split(',');
      try {
        conditionsArr.forEach((c: string) => {
          const condition = c.split(/(\=|\<|\>)/);
          if (condition.length === 1 && !answersVars?.[condition[0]]) {
            throw new Error('');
          }

          const [conditionVar, conditionStatement] = condition;
          const conditionValue = condition[2].replace(/^["'](.+(?=["']$))["']$/, '$1');

          switch (conditionStatement) {
            case '=':
              if (!answersVars?.[conditionVar] && answersVars?.[conditionVar] !== conditionValue) {
                throw new Error('');
              }
              break;
            case '>':
              if (!answersVars?.[conditionVar] && answersVars?.[conditionVar]! >= conditionValue) throw new Error('');
              break;
            case '<':
              if (!answersVars?.[conditionVar] && answersVars?.[conditionVar]! <= conditionValue) throw new Error('');
              break;
          }
        });
      } catch (e) {
        return false;
      }
      return true;
    },
    [answersVars, answerConditions]
  );

  return (
    <IonPage>
      {!currentQuestion?.hideHeader && <OnBoardingHeader hideBackButton={currentQuestion?.hideBackButton} />}
      <IonContent className={'white'}>
        <Container>
          <QuestionWrapper
            style={{
              paddingBottom: currentQuestion?.answers.find((a) => a.popup) ? '80px' : 0,
            }}
          >
            <div className="question-header">
              <h3 className="question">{currentQuestion?.question}</h3>
              <div
                className="description"
                dangerouslySetInnerHTML={{
                  __html:
                    currentQuestion?.description.replace(/{{(\w*)}}/g, (m, key) =>
                      answersVars.hasOwnProperty(key) ? answersVars[key] : m
                    ) ?? '',
                }}
              />
              {currentQuestion?.is_multiple && (
                <div className="several">
                  <FormattedMessage id={'on-boarding.chose.several'} defaultMessage={'Можна обрати кілька варіантів'} />
                </div>
              )}
            </div>
            <ul className="answers">
              {currentQuestion?.answers.filter(isAnswerAvailable).map((a, index) => (
                <li key={index}>
                  <Answer
                    type={currentQuestion.is_multiple ? 'checkbox' : 'radio'}
                    name={'answer'}
                    label={a.title}
                    id={a.id}
                    onClick={handleAnswer}
                    defaultChecked={!!selectedAnswers?.find((i) => i === a.id)}
                    isAnimated={currentQuestion.hideContinueButton}
                  />
                </li>
              ))}
            </ul>
            {currentQuestion?.img_link && (
              <div className="img-wrapper">
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                <img src={currentQuestion.img_link} alt="question image" />
              </div>
            )}
          </QuestionWrapper>
        </Container>
      </IonContent>
      {currentQuestion?.hideContinueButton !== true && (
        <OnBoardingFooter isPopup={!!answerPopup}>
          {answerPopup && <AnswerPopupWrapper>{answerPopup}</AnswerPopupWrapper>}
          <CustomButton
            type="button"
            buttonType="primary"
            onClick={async () => {
              isomorficHapticsImpact({ style: ImpactStyle.Light });
              await handleNextStep();
            }}
            disabled={!selectedAnswers?.length || isLoadingSelector}
            loading={isLoadingSelector}
          >
            <FormattedMessage id={'general.btn.next'} defaultMessage={'Далі'} />
          </CustomButton>
        </OnBoardingFooter>
      )}
    </IonPage>
  );
};

export default React.memo(OnBoardingQuestion);
